'use client';
import { NotificationService } from '@capsicum/common';
import React, { type FC, useEffect, useMemo } from 'react';
import styled, { css, type RuleSet, type StyledObject } from 'styled-components';

import { useWindowSize } from '../../../hooks/helpers/useWindowSize';
import { TOPBAR_ZINDEX } from '../../atoms/top-bar/TopBar';

import { Motion } from './Motion';

export const NotificationsWrapper: FC<Record<string, never>> = ({ testID }) => {
	const { orientation, size } = useWindowSize();
	const notifications = NotificationService.useNotifications();

	const sortedList = useMemo(
		() => Array.from(notifications.entries()).sort((a, b) => b[1].priority - a[1].priority),
		[notifications]
	);

	useEffect(() => {
		const upfrontNotif = sortedList[0];

		if (upfrontNotif) NotificationService.setTimerOrPersist(upfrontNotif[0]);
	}, [sortedList]);

	const widthStyling = useMemo(() => {
		if (orientation === 'portrait') {
			switch (size) {
				case 'mobile':
					return css`
						width: 100%;
					`;
				case 'tablet':
					return css`
						width: 50%;
					`;
				default:
					return css`
						width: 30%;
					`;
			}
		} else {
			switch (size) {
				case 'mobile':
					return css`
						width: 50%;
					`;
				default:
					return css`
						width: 30%;
					`;
			}
		}
	}, [orientation, size]);

	const firstNotification = sortedList?.[0];

	if (!sortedList?.length || !firstNotification) {
		return null;
	}

	return (
		<Container
			$containerStyle={firstNotification[1].containerStyle}
			data-testid={testID}
			key={firstNotification[0]}
		>
			<Item $widthStyling={widthStyling}>
				<Motion {...firstNotification[1].component.props} {...firstNotification[1]}>
					{firstNotification[1].component}
				</Motion>
			</Item>
		</Container>
	);
};

const Container = styled.div<{ $containerStyle?: StyledObject<object> }>`
	position: fixed;
	padding: ${({ theme }) => theme.spacings.medium};
	top: 0;
	width: 100%;
	display: flex;
	justify-content: center;
	z-index: ${TOPBAR_ZINDEX + 1};
	${({ $containerStyle }) => $containerStyle};
`;

const Item = styled.div<{ $widthStyling: RuleSet<object> }>`
	${({ $widthStyling }) => $widthStyling};
`;
